/**
 * MM Cart Progress Bar - Moon Moon Agency
 * Custom element for the cart-drawer progress bar.
 *
 * The track/fill only represent the free-shipping goal (cart price vs. its threshold) — no marker,
 * since the bar exists solely for the shipping goal.
 * Below the track, one message per free_product block is always visible and kept in sync with
 * the cart (quantity changes, additions, removals), independently of the shipping bar above —
 * counting down to the free product ("palier") until it's reached.
 *
 * Data sources:
 * - "cart:change" (native Impact event, detail.cart) for the shipping goal
 * - "free-product:progress" (dispatched by mm_free_product.js, detail.tiers) for product tiers
 */
class MMCartProgressBar extends HTMLElement {
	connectedCallback() {
		this._fillElement = this.querySelector('[data-fill]');
		this._shippingLabelElement = this.querySelector('[data-tier-label][data-tier-type="shipping"]');
		this._quantityMessageElements = Array.from(this.querySelectorAll('[data-quantity-message]'));

		this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
			style: 'currency',
			currency: Shopify.currency.active,
		});

		this._shippingState = this._initShippingState();

		this._onCartChangedListener = this._onCartChanged.bind(this);
		this._onProductProgressListener = this._onProductProgress.bind(this);
		document.addEventListener('cart:change', this._onCartChangedListener);
		document.addEventListener('free-product:progress', this._onProductProgressListener);

		this._renderShipping();

		// Impact recreates this element on every cart-drawer HTML swap, which can happen right
		// after mm_free_product.js has already dispatched "free-product:progress" for this same
		// cart change (see the comment on FreeProductManager.refresh()). Pulling fresh state here
		// closes that gap instead of leaving raw, unsubstituted template text on screen.
		window.FreeProductManager?.refresh();
	}

	disconnectedCallback() {
		document.removeEventListener('cart:change', this._onCartChangedListener);
		document.removeEventListener('free-product:progress', this._onProductProgressListener);
	}

	_initShippingState() {
		if (!this._fillElement) return null;

		const thresholdCents = Math.round(
			parseFloat(this._fillElement.dataset.thresholdCents) * (Shopify.currency.rate || 1)
		);

		return {
			thresholdCents,
			currentCents: parseFloat(this._fillElement.dataset.currentCents) || 0,
		};
	}

	_onCartChanged(event) {
		if (!this._shippingState) return;

		const cart = event.detail?.cart;
		if (!cart?.items) return;

		const priceForItems = cart.items
			.filter((item) => item.requires_shipping)
			.reduce((sum, item) => sum + item.final_line_price, 0);
		const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
			(sum, discount) => sum + discount.total_allocated_amount,
			0
		);

		this._shippingState.currentCents = priceForItems - cartDiscount;
		this._renderShipping();
	}

	_renderShipping() {
		if (!this._shippingState) return;

		const { thresholdCents, currentCents } = this._shippingState;
		const ratio = thresholdCents > 0 ? currentCents / thresholdCents : 0;
		const clampedRatio = Math.min(1, Math.max(0, ratio));
		const reached = currentCents >= thresholdCents;

		this._fillElement.style.width = `${clampedRatio * 100}%`;
		this._fillElement.classList.toggle('mm-cart-progress-bar__fill--reached', reached);

		if (!this._shippingLabelElement) return;

		const messageElement = this._shippingLabelElement.querySelector('[data-tier-message]');
		if (!messageElement) return;

		messageElement.innerHTML = reached
			? messageElement.dataset.reachedText
			: this._buildShippingMessage(messageElement);
	}

	_buildShippingMessage(messageElement) {
		const template = messageElement.dataset.unreachedTextTemplate;
		if (!template) return '';

		const { thresholdCents, currentCents } = this._shippingState;
		const remaining = (thresholdCents - currentCents) / 100;
		const replacement = this._currencyFormatter.format(remaining);

		return template.replace('[MONTANT]', this._wrapHighlight(replacement));
	}

	/**
	 * Keep each free_product message's text in sync with the cart — not tied to whether a
	 * trigger product is currently in the cart. Hides entirely (both before and after the tier
	 * is reached) whenever the merchant left the corresponding text setting blank, instead of
	 * showing an empty pill. The free product itself is still added/removed independently by
	 * mm_free_product.js regardless of whether any message is shown.
	 */
	_onProductProgress(event) {
		const tiers = event.detail?.tiers;
		if (!tiers) return;

		const tiersByVariantId = new Map(tiers.map((tier) => [String(tier.variantId), tier]));

		for (const messageElement of this._quantityMessageElements) {
			const tier = tiersByVariantId.get(messageElement.dataset.variantId);
			const messageTextElement = messageElement.querySelector('[data-tier-message]');
			if (!tier || !messageTextElement) continue;

			if (tier.reached) {
				const reachedText = messageTextElement.dataset.reachedText || '';
				messageElement.hidden = reachedText === '';
				messageTextElement.innerHTML = reachedText;
			} else {
				const unreachedText = this._buildQuantityMessage(messageTextElement, tier);
				messageElement.hidden = unreachedText === '';
				messageTextElement.innerHTML = unreachedText;
			}
		}
	}

	_buildQuantityMessage(messageTextElement, tier) {
		const template = messageTextElement.dataset.unreachedTextTemplate;
		if (!template) return '';

		const remaining = Math.max(0, tier.threshold - tier.triggerCount);
		return template.replace('[X]', this._wrapHighlight(remaining));
	}

	_wrapHighlight(value) {
		return `<span class="mm-cart-progress-bar__highlight">${value}</span>`;
	}
}

if (!window.customElements.get('mm-cart-progress-bar')) {
	window.customElements.define('mm-cart-progress-bar', MMCartProgressBar);
}
